import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { Button, ConfirmButton, DataTable, type ColumnConfig } from '@/components/ui';
import styles from './ProfileSessionsTab.module.css';
import shared from './Profile.module.css';
import type { KeycloakSession } from '@/api/profile';
import { formatSessionTime } from './sessionFormat';

export interface ProfileSessionsTabProps {
    isLoading: boolean;
    softError: boolean;
    isSuccess: boolean;
    sessions: KeycloakSession[];
    currentSid?: string;
    endingSessionId?: string;
    onEndSession: (sessionId: string) => void;
    onOpenLogoutAllModal: () => void;
    onOpenPasswordModal: () => void;
}

function DeviceIcon() {
    return (
        <svg viewBox="0 0 24 24" width="16" height="16" fill="none" stroke="currentColor" strokeWidth="1.8" aria-hidden="true">
            <rect x="3" y="4" width="18" height="12" rx="2" />
            <path d="M8 20h8" strokeLinecap="round" />
        </svg>
    );
}

function LogoutIcon() {
    return (
        <svg viewBox="0 0 24 24" width="14" height="14" fill="none" stroke="currentColor" strokeWidth="1.9" aria-hidden="true">
            <path d="M15 5V4a1 1 0 0 0-1-1H5a1 1 0 0 0-1 1v16a1 1 0 0 0 1 1h9a1 1 0 0 0 1-1v-1" strokeLinecap="round" />
            <path d="M10 12h11m0 0-3-3m3 3-3 3" strokeLinecap="round" strokeLinejoin="round" />
        </svg>
    );
}

function WarnIcon() {
    return (
        <svg viewBox="0 0 24 24" width="14" height="14" fill="none" stroke="currentColor" strokeWidth="1.9" aria-hidden="true">
            <path d="M12 4 2.5 20h19L12 4z" strokeLinejoin="round" />
            <path d="M12 10v4M12 17h.01" strokeLinecap="round" />
        </svg>
    );
}

/**
 * `/profile` — "Phiên đăng nhập" tab (frame 79) + the "Đăng xuất mọi thiết bị"
 * confirm (frame 82, rendered by the caller as `LogoutAllDevicesModal`).
 *
 * EAA-1820: restyled into frame 79's table (Thiết bị / Địa chỉ IP / Bắt đầu /
 * Hoạt động gần nhất / action) from the pre-tab per-row-card list EAA-1822
 * shipped. The "Thiết bị" column is the one honest gap: `KeycloakSession`
 * (`@/api/profile`, pinned to `KeycloakSessionSerializer`) carries
 * `id/ip_address/start/last_access/clients` — no browser or OS string. The
 * frame's "Chrome trên macOS" is sample copy; this renders the real
 * `clients` (which app the session belongs to) as the row's label instead of
 * fabricating a user-agent parse the backend does not send.
 */
export function ProfileSessionsTab({
    isLoading,
    softError,
    isSuccess,
    sessions,
    currentSid,
    endingSessionId,
    onEndSession,
    onOpenLogoutAllModal,
    onOpenPasswordModal,
}: ProfileSessionsTabProps) {
    const { t } = useTranslation();

    // EAA-1804: kit `DataTable` replacing the hand-rolled
    // `<table className="portal-profile-sessions-table">` — this list is a
    // per-user, unbounded set (every open Keycloak session), the exact shape
    // `AppsTable`'s own migration doc comment calls out. `pagination={false}`
    // matches the previous behaviour (portal-api has no session pagination),
    // and `rowClassName` replaces the old `tr.is-current-device` selector.
    // EAA-1846: that rule now lives in this file's CSS module, so `rowClassName`
    // hands `DataTable` the hashed class directly and the old wrapper selector
    // reaching through the table into its `<tr>`s is gone; highlight unchanged.
    const columns: ColumnConfig<KeycloakSession>[] = useMemo(
        () => [
            {
                key: 'device',
                title: t('profile.sessionsTab.deviceHeader'),
                render: (_value, session) => {
                    const isThisDevice = Boolean(currentSid) && session.id === currentSid;
                    return (
                        <span className={styles.deviceCell}>
                            <span className={styles.deviceIcon}>
                                <DeviceIcon />
                            </span>
                            <span className={styles.deviceLabels}>
                                <span className={styles.deviceName}>
                                    {session.clients && session.clients.length > 0
                                        ? session.clients.join(', ')
                                        : t('profile.unknownApp')}
                                    {isThisDevice && (
                                        <span className={styles.currentTag}>
                                            {t('profile.thisDevice')}
                                        </span>
                                    )}
                                </span>
                            </span>
                        </span>
                    );
                },
            },
            {
                key: 'ip',
                title: t('profile.sessionsTab.ipHeader'),
                render: (_value, session) =>
                    session.ip_address ? (
                        <code>{session.ip_address}</code>
                    ) : (
                        <span className="portal-muted">{t('profile.unknownIp')}</span>
                    ),
            },
            {
                key: 'start',
                title: t('profile.sessionsTab.startHeader'),
                render: (_value, session) =>
                    formatSessionTime(session.start, t('profile.unknownAccessTime')),
            },
            {
                key: 'lastActive',
                title: t('profile.sessionsTab.lastActiveHeader'),
                render: (_value, session) =>
                    formatSessionTime(session.last_access, t('profile.unknownAccessTime')),
            },
        ],
        [currentSid, t]
    );

    return (
        <>
            <div className={shared.notice}>
                <InfoIcon />
                <span>
                    {t('profile.sessionsTab.ownSessionsNoticePrefix')}
                    <strong>{t('profile.sessionsTab.ownSessionsNoticeBold')}</strong>
                    {t('profile.sessionsTab.ownSessionsNoticeSuffix')}
                </span>
            </div>

            <div className={`${shared.card} ${shared.cardFlush}`}>
                <div className={styles.sessionsHeader}>
                    <span className={shared.infoCardTitle}>
                        {t('profile.sessionsTab.openSessionsTitle', { count: sessions.length })}
                    </span>
                    <span className={shared.spacer} />
                    {/* EAA-1804: kit `Button` instead of hand-written `<button className="portal-btn">`. */}
                    <Button danger onClick={onOpenLogoutAllModal} disabled={sessions.length === 0}>
                        <LogoutIcon />
                        {t('profile.logoutAll')}
                    </Button>
                </div>

                {isLoading && <p aria-busy="true">{t('profile.sessionsCard.loading')}</p>}
                {softError && (
                    <p className="portal-profile-soft-error">{t('profile.sessionsCard.error')}</p>
                )}
                {isSuccess && sessions.length === 0 && (
                    <p className="portal-profile-empty">{t('profile.sessionsCard.empty')}</p>
                )}

                {sessions.length > 0 && (
                    <div className="portal-table-scroll">
                        <DataTable<KeycloakSession>
                            data={sessions}
                            columns={columns}
                            rowKey="id"
                            pagination={false}
                            rowClassName={(session) =>
                                Boolean(currentSid) && session.id === currentSid
                                    ? styles.currentDeviceRow
                                    : ''
                            }
                            actions={(session) => {
                                const isThisDevice = Boolean(currentSid) && session.id === currentSid;
                                const ending = endingSessionId === session.id;
                                if (isThisDevice) {
                                    return <span className="portal-muted">—</span>;
                                }
                                return (
                                    <ConfirmButton
                                        label={ending ? t('profile.endingSession') : t('profile.endSession')}
                                        danger
                                        confirmTitle={t('profile.endSessionConfirmTitle')}
                                        confirmDescription={t('profile.endSessionConfirmOtherDevice')}
                                        disabled={Boolean(endingSessionId)}
                                        onConfirm={() => onEndSession(session.id)}
                                    />
                                );
                            }}
                        />
                    </div>
                )}

                <div className={styles.sessionsFootnote}>
                    <WarnIcon />
                    {t('profile.sessionsTab.footerWarning')}
                </div>
            </div>

            <div className={`${shared.card} ${shared.passwordCard}`}>
                <span className={shared.infoCardTitle}>
                    {t('profile.infoTab.passwordCardTitle')}
                </span>
                <span className={shared.infoCardDesc}>
                    {t('profile.sessionsTab.passwordCardDesc')}
                </span>
                <Button className={styles.passwordBtn} onClick={onOpenPasswordModal}>
                    {t('profile.changePassword')}
                </Button>
            </div>
        </>
    );
}

function InfoIcon() {
    return (
        <svg
            viewBox="0 0 24 24"
            width="17"
            height="17"
            fill="none"
            stroke="currentColor"
            strokeWidth="1.8"
            aria-hidden="true"
            className={shared.noticeIcon}
        >
            <circle cx="12" cy="12" r="9" />
            <path d="M12 11v5M12 8h.01" strokeLinecap="round" />
        </svg>
    );
}

export default ProfileSessionsTab;
